import { inferenceHttp } from '@/lib/api/inference-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import {
  AgentsService,
  ConnectMcpResponseDto,
  InstallMcpDto,
  InstallMcpResponseDto,
  McpCatalogResponseDto,
  McpCatalogService,
  McpServerDto,
  TestMcpConnectionResponseDto,
  ToolsService,
  UserMcpServersService,
} from '@inference.engine/api-client/web';

/**
 * The catalog MCPs the current user can install. The catalog GET is open to any
 * authenticated user (scoped to their org), so the user feature calls it directly
 * rather than reaching into the admin feature.
 */
export const getInstallableCatalog = async (): Promise<McpCatalogResponseDto[]> => {
  const response = await McpCatalogService.list({ client: inferenceHttp });
  return response.data ?? [];
};

/** The current user's own installations (one per catalog MCP they've installed). */
export const getInstalledMcps = async (): Promise<InstallMcpResponseDto[]> => {
  const response = await UserMcpServersService.list({ client: inferenceHttp });
  return response.data ?? [];
};

/**
 * The built-in (platform) MCP servers — always available, not user-managed. Derived on the FE by
 * filtering the available-servers list to its `builtIn` entries: "list built-ins separately" is a
 * view decision, so we slice the BE's tagged list rather than add a BE endpoint per screen. The
 * org catalog (the non-built-in entries) is consumed via `getInstallableCatalog` instead, with its
 * full `McpCatalogResponseDto` shape — so callers never see catalog rows under this lean schema.
 */
export const getBuiltInMcpServers = async (): Promise<McpServerDto[]> => {
  const response = await ToolsService.listMcpServers({ client: inferenceHttp });
  return (response.data ?? []).filter((server) => server.builtIn);
};

/**
 * The catalog MCP ids assigned to an agent (`metadata.mcpServers`). Fetched directly via the
 * user-defined-agents service so the chat composer can resolve an agent's MCP status without
 * reaching into the admin feature.
 */
export const getAgentAssignedMcpIds = async (agentId: string): Promise<string[]> => {
  const response = await AgentsService.getAgentById({
    client: inferenceHttp,
    path: { id: agentId },
    // A card the caller cannot see only means no status to show, never an error over the chat.
    // eslint-disable-next-line i18next/no-literal-string -- header value
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });
  return response.data?.mcpServerIds ?? [];
};

const toBody = (headers?: Record<string, string>): InstallMcpDto =>
  headers && Object.keys(headers).length > 0 ? { headers } : {};

/** Install a catalog MCP for the current user, optionally with static auth headers. */
export const installMcp = async (
  mcpCatalogId: string,
  headers?: Record<string, string>,
): Promise<InstallMcpResponseDto> => {
  const response = await UserMcpServersService.install({
    client: inferenceHttp,
    path: { id: mcpCatalogId },
    body: toBody(headers),
  });
  return response.data!;
};

/** Replace the stored auth headers for an already-installed MCP. */
export const updateMcpCredentials = async (
  mcpCatalogId: string,
  headers?: Record<string, string>,
): Promise<InstallMcpResponseDto> => {
  const response = await UserMcpServersService.update({
    client: inferenceHttp,
    path: { id: mcpCatalogId },
    body: toBody(headers),
  });
  return response.data!;
};

/**
 * Starts the OAuth authorization for a catalog `oauth` MCP. Returns the authorize URL for the FE to
 * open in a popup; the callback parks the authorization response, and {@link completeOauthConnect}
 * finishes the exchange under the user's own session.
 */
export const connectMcp = async (mcpCatalogId: string): Promise<ConnectMcpResponseDto> => {
  const response = await UserMcpServersService.connect({
    client: inferenceHttp,
    path: { id: mcpCatalogId },
  });
  return response.data!;
};

/**
 * Completes an OAuth flow the current user started, identified by its `state`. Authenticated on
 * purpose: the server verifies the flow belongs to the caller before exchanging the parked code,
 * which is what stops a consent lured out of someone else landing tokens on this user's install.
 */
export const completeOauthConnect = async (state: string): Promise<{ mcpCatalogId: string }> => {
  const response = await UserMcpServersService.completeOAuthConnect({
    client: inferenceHttp,
    body: { state },
  });
  return response.data!;
};

/** Uninstall (or, for OAuth, disconnect) an MCP for the current user. */
export const uninstallMcp = async (mcpCatalogId: string): Promise<void> => {
  await UserMcpServersService.uninstall({ client: inferenceHttp, path: { id: mcpCatalogId } });
};

/**
 * Tests the live connection to an installed MCP with the user's stored credentials. Returns a
 * categorized result ({ status: 'connected' | 'error', errorType? }); the result is briefly cached
 * server-side.
 *
 * Global error toasts are suppressed: this is a background probe whose failures are rendered as
 * row/badge state. Without the header, an expected-during-transition error — e.g. the 404 a mounted
 * health query hits when an admin deletes its catalog entry (the ['user-mcps'] invalidation
 * refetches it before the refreshed lists unmount it) — surfaces as a spurious global toast.
 */
export const testMcpConnection = async (
  mcpCatalogId: string,
): Promise<TestMcpConnectionResponseDto> => {
  const response = await UserMcpServersService.test({
    client: inferenceHttp,
    path: { id: mcpCatalogId },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });
  return response.data!;
};
